import { TrendingUp, TrendingDown } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  badge?: {
    text: string;
    variant: 'success' | 'error' | 'warning' | 'info';
  };
  action?: {
    label: string;
    onClick: () => void;
  };
  valueColor?: string;
  /** Cor do ícone e da barra de progresso. Default: primary. */
  accent?: 'primary' | 'tertiary' | 'error' | 'success' | 'warning' | 'info';
  /** Quando presente, mostra uma barra de progresso linear (razão) abaixo do título. */
  progress?: { value: number; max: number };
}

const accentText = {
  primary: 'text-primary',
  tertiary: 'text-tertiary',
  error: 'text-error',
  success: 'text-success',
  warning: 'text-warning',
  info: 'text-info',
} as const;

const accentBar = {
  primary: 'bg-primary',
  tertiary: 'bg-tertiary',
  error: 'bg-error',
  success: 'bg-success',
  warning: 'bg-warning',
  info: 'bg-info',
} as const;

/** Trilha = mesmo tom do accent, mais claro — não cinza genérico (o estado lê no bar inteiro). */
const accentTrack = {
  primary: 'bg-primary/15',
  tertiary: 'bg-tertiary/15',
  error: 'bg-error/15',
  success: 'bg-success/15',
  warning: 'bg-warning/15',
  info: 'bg-info/15',
} as const;

const badgeVariants = {
  success: 'text-success bg-success/10',
  error: 'text-error bg-error/10',
  warning: 'text-warning bg-warning/10',
  info: 'text-info bg-info/10',
} as const;

export function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  badge,
  action,
  valueColor,
  accent = 'primary',
  progress,
}: MetricCardProps) {
  const pct = progress ? (progress.max > 0 ? (progress.value / progress.max) * 100 : 0) : 0;
  return (
    <div className="flex h-full flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-card transition-shadow duration-200 hover:shadow-card-hover sm:p-5">
      {/* Value — sempre no topo, sozinho, sem disputar espaço com ícone */}
      <p
        className={`text-3xl font-bold tracking-tight sm:text-4xl ${
          valueColor || 'text-text-title'
        }`}
      >
        {value}
      </p>

      {/* Título com ícone pequeno inline (sem badge/círculo) */}
      <div className="flex items-center gap-1.5">
        <Icon size={14} className={`shrink-0 ${accentText[accent]}`} />
        <p className="text-sm font-medium leading-tight text-text-secondary sm:text-base">
          {title}
        </p>
      </div>

      {/* Barra de progresso linear — substitui o anel */}
      {progress && (
        <div className={`h-1 w-full overflow-hidden rounded-full ${accentTrack[accent]}`}>
          <div
            className={`h-full rounded-full transition-[width] duration-500 ${accentBar[accent]}`}
            style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
          />
        </div>
      )}

      {/* Footer — cola no rodapé quando o card estica pra igualar a altura dos vizinhos */}
      <div className="mt-auto flex items-center justify-between gap-3">
        <p className="text-xs text-text-secondary">{subtitle}</p>

        {trend && (
          <span
            className={`flex items-center gap-1 text-sm font-semibold tabular-nums ${
              trend.isPositive ? 'text-success' : 'text-error'
            }`}
          >
            {trend.isPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {trend.isPositive ? '+' : ''}
            {trend.value}
          </span>
        )}

        {badge && (
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${badgeVariants[badge.variant]}`}
          >
            {badge.text}
          </span>
        )}

        {action && (
          <button
            onClick={action.onClick}
            className="text-sm font-semibold text-primary transition-colors hover:text-primary/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}
